import React, { useState, useEffect, useRef, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPen,
  faTimes,
  faTrash,
  faEraser,
  faUndo,
  faRedo,
  faDownload,
  faSquare,
  faCircle,
  faMinus,
  faFont,
  faFill,
  faPaintBrush,
  faHighlighter,
  faExpand,
  faCompress,
} from "@fortawesome/free-solid-svg-icons";
import "./Scratchpad.css";

const TOOLS = {
  PEN: "pen",
  BRUSH: "brush",
  HIGHLIGHTER: "highlighter",
  ERASER: "eraser",
  LINE: "line",
  RECT: "rect",
  CIRCLE: "circle",
  TEXT: "text",
  FILL: "fill",
};

const COLORS = [
  "#000000", "#ffffff", "#ef4444", "#f97316", "#eab308",
  "#22c55e", "#3b82f6", "#8b5cf6", "#ec4899", "#6b7280",
  "#7c3aed", "#0ea5e9", "#14b8a6", "#a3e635", "#f43f5e",
  "#6366f1", "#84cc16", "#06b6d4", "#6366f1", "#d946ef",
];

const Scratchpad = ({ externalIsOpen, onCloseExternal, hideFab, canDownload }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;

  const handleSetIsOpen = (val) => {
    if (isControlled) {
      if (!val && onCloseExternal) onCloseExternal();
    } else {
      setInternalIsOpen(val);
    }
  };

  // Drawing state
  const canvasRef = useRef(null);
  const overlayCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const canvasAreaRef = useRef(null);
  const [tool, setTool] = useState(TOOLS.PEN);
  const [color, setColor] = useState("#000000");
  const [brushSize, setBrushSize] = useState(3);
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [startPos, setStartPos] = useState(null);
  const [textInput, setTextInput] = useState("");
  const [textPos, setTextPos] = useState(null);
  const [showTextInput, setShowTextInput] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [customColor, setCustomColor] = useState("#000000");
  const [canvasSize, setCanvasSize] = useState({ w: 800, h: 600 });
  const hasInitialized = useRef(false);

  const CANVAS_W = canvasSize.w;
  const CANVAS_H = canvasSize.h;

  // Resize canvas to fill available area
  const resizeCanvas = useCallback(() => {
    if (!canvasAreaRef.current || !canvasRef.current) return;
    const area = canvasAreaRef.current;
    const padding = 24; // 12px each side
    const newW = Math.floor(area.clientWidth - padding);
    const newH = Math.floor(area.clientHeight - padding);
    if (newW < 100 || newH < 100) return;

    const canvas = canvasRef.current;
    const overlay = overlayCanvasRef.current;

    // Save current drawing before resize
    let existingData = null;
    if (hasInitialized.current) {
      existingData = canvas.toDataURL();
    }

    canvas.width = newW;
    canvas.height = newH;
    overlay.width = newW;
    overlay.height = newH;

    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, newW, newH);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    // Restore previous drawing
    if (existingData) {
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0);
      };
      img.src = existingData;
    }

    setCanvasSize({ w: newW, h: newH });
  }, []);

  // Initialize canvas and observe size changes
  useEffect(() => {
    if (isOpen && canvasRef.current && canvasAreaRef.current) {
      // Small delay to let the panel animate in
      const timer = setTimeout(() => {
        resizeCanvas();
        if (!hasInitialized.current) {
          hasInitialized.current = true;
          saveToHistory();
        }
      }, 50);

      const observer = new ResizeObserver(() => {
        resizeCanvas();
      });
      observer.observe(canvasAreaRef.current);

      return () => {
        clearTimeout(timer);
        observer.disconnect();
      };
    }
    if (!isOpen) {
      hasInitialized.current = false;
    }
    // eslint-disable-next-line
  }, [isOpen, isExpanded]);

  const saveToHistory = useCallback(() => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const imageData = canvas.toDataURL();
    setHistory((prev) => {
      const newHistory = prev.slice(0, historyIndex + 1);
      newHistory.push(imageData);
      return newHistory;
    });
    setHistoryIndex((prev) => prev + 1);
  }, [historyIndex]);

  const undo = () => {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    restoreFromHistory(newIndex);
  };

  const redo = () => {
    if (historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    restoreFromHistory(newIndex);
  };

  const restoreFromHistory = (index) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    };
    img.src = history[index];
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    saveToHistory();
  };

  const downloadCanvas = () => {
    const canvas = canvasRef.current;
    const link = document.createElement("a");
    link.download = "scratchpad-drawing.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  // Get mouse/touch position relative to canvas
  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    let clientX, clientY;
    if (e.touches) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const getCtx = () => canvasRef.current?.getContext("2d");
  const getOverlayCtx = () => overlayCanvasRef.current?.getContext("2d");

  const setupStroke = (ctx, currentTool, currentColor, currentSize) => {
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (currentTool === TOOLS.ERASER) {
      ctx.globalCompositeOperation = "destination-out";
      ctx.strokeStyle = "rgba(0,0,0,1)";
      ctx.lineWidth = currentSize * 3;
      ctx.globalAlpha = 1;
    } else if (currentTool === TOOLS.HIGHLIGHTER) {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = currentSize * 4;
      ctx.globalAlpha = 0.3;
    } else if (currentTool === TOOLS.BRUSH) {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = currentSize * 2;
      ctx.globalAlpha = 0.8;
    } else {
      ctx.globalCompositeOperation = "source-over";
      ctx.strokeStyle = currentColor;
      ctx.lineWidth = currentSize;
      ctx.globalAlpha = 1;
    }
  };

  const resetCtx = (ctx) => {
    ctx.globalCompositeOperation = "source-over";
    ctx.globalAlpha = 1;
  };

  // Flood fill algorithm
  const floodFill = (startX, startY, fillColor) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const imageData = ctx.getImageData(0, 0, CANVAS_W, CANVAS_H);
    const data = imageData.data;

    const sx = Math.floor(startX);
    const sy = Math.floor(startY);
    const startIdx = (sy * CANVAS_W + sx) * 4;
    const startR = data[startIdx];
    const startG = data[startIdx + 1];
    const startB = data[startIdx + 2];
    const startA = data[startIdx + 3];

    // Parse fill color
    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = 1;
    tempCanvas.height = 1;
    const tempCtx = tempCanvas.getContext("2d");
    tempCtx.fillStyle = fillColor;
    tempCtx.fillRect(0, 0, 1, 1);
    const fillData = tempCtx.getImageData(0, 0, 1, 1).data;
    const fillR = fillData[0];
    const fillG = fillData[1];
    const fillB = fillData[2];

    if (startR === fillR && startG === fillG && startB === fillB && startA === 255) return;

    const tolerance = 30;
    const match = (idx) => {
      return (
        Math.abs(data[idx] - startR) <= tolerance &&
        Math.abs(data[idx + 1] - startG) <= tolerance &&
        Math.abs(data[idx + 2] - startB) <= tolerance &&
        Math.abs(data[idx + 3] - startA) <= tolerance
      );
    };

    const stack = [[sx, sy]];
    const visited = new Set();

    while (stack.length > 0) {
      const [x, y] = stack.pop();
      const key = y * CANVAS_W + x;
      if (visited.has(key)) continue;
      if (x < 0 || x >= CANVAS_W || y < 0 || y >= CANVAS_H) continue;
      const idx = key * 4;
      if (!match(idx)) continue;

      visited.add(key);
      data[idx] = fillR;
      data[idx + 1] = fillG;
      data[idx + 2] = fillB;
      data[idx + 3] = 255;

      stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
    }

    ctx.putImageData(imageData, 0, 0);
    saveToHistory();
  };

  const handleMouseDown = (e) => {
    e.preventDefault();
    const pos = getPos(e);

    if (tool === TOOLS.FILL) {
      floodFill(pos.x, pos.y, color);
      return;
    }

    if (tool === TOOLS.TEXT) {
      setTextPos(pos);
      setShowTextInput(true);
      setTextInput("");
      return;
    }

    setIsDrawing(true);
    setStartPos(pos);

    if ([TOOLS.PEN, TOOLS.BRUSH, TOOLS.HIGHLIGHTER, TOOLS.ERASER].includes(tool)) {
      const ctx = getCtx();
      setupStroke(ctx, tool, color, brushSize);
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const pos = getPos(e);

    if ([TOOLS.PEN, TOOLS.BRUSH, TOOLS.HIGHLIGHTER, TOOLS.ERASER].includes(tool)) {
      const ctx = getCtx();
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
    } else if ([TOOLS.LINE, TOOLS.RECT, TOOLS.CIRCLE].includes(tool)) {
      // Draw preview on overlay
      const overlayCtx = getOverlayCtx();
      overlayCtx.clearRect(0, 0, CANVAS_W, CANVAS_H);
      overlayCtx.strokeStyle = color;
      overlayCtx.lineWidth = brushSize;
      overlayCtx.lineCap = "round";
      overlayCtx.lineJoin = "round";
      overlayCtx.beginPath();

      if (tool === TOOLS.LINE) {
        overlayCtx.moveTo(startPos.x, startPos.y);
        overlayCtx.lineTo(pos.x, pos.y);
      } else if (tool === TOOLS.RECT) {
        overlayCtx.rect(startPos.x, startPos.y, pos.x - startPos.x, pos.y - startPos.y);
      } else if (tool === TOOLS.CIRCLE) {
        const rx = Math.abs(pos.x - startPos.x) / 2;
        const ry = Math.abs(pos.y - startPos.y) / 2;
        const cx = startPos.x + (pos.x - startPos.x) / 2;
        const cy = startPos.y + (pos.y - startPos.y) / 2;
        overlayCtx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      }
      overlayCtx.stroke();
    }
  };

  const handleMouseUp = (e) => {
    if (!isDrawing) return;
    setIsDrawing(false);

    const ctx = getCtx();
    resetCtx(ctx);

    if ([TOOLS.LINE, TOOLS.RECT, TOOLS.CIRCLE].includes(tool)) {
      const pos = getPos(e);
      const overlayCtx = getOverlayCtx();
      overlayCtx.clearRect(0, 0, CANVAS_W, CANVAS_H);

      ctx.strokeStyle = color;
      ctx.lineWidth = brushSize;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.beginPath();

      if (tool === TOOLS.LINE) {
        ctx.moveTo(startPos.x, startPos.y);
        ctx.lineTo(pos.x, pos.y);
      } else if (tool === TOOLS.RECT) {
        ctx.rect(startPos.x, startPos.y, pos.x - startPos.x, pos.y - startPos.y);
      } else if (tool === TOOLS.CIRCLE) {
        const rx = Math.abs(pos.x - startPos.x) / 2;
        const ry = Math.abs(pos.y - startPos.y) / 2;
        const cx = startPos.x + (pos.x - startPos.x) / 2;
        const cy = startPos.y + (pos.y - startPos.y) / 2;
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      }
      ctx.stroke();
    }

    saveToHistory();
    setStartPos(null);
  };

  const handleTextSubmit = () => {
    if (!textInput.trim() || !textPos) return;
    const ctx = getCtx();
    ctx.font = `${brushSize * 5}px 'Zain', sans-serif`;
    ctx.fillStyle = color;
    ctx.textBaseline = "top";
    ctx.fillText(textInput, textPos.x, textPos.y);
    setShowTextInput(false);
    setTextInput("");
    setTextPos(null);
    saveToHistory();
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === "Escape") {
        if (showTextInput) {
          setShowTextInput(false);
        } else {
          handleSetIsOpen(false);
        }
      }
      if (e.ctrlKey && e.key === "z") {
        e.preventDefault();
        undo();
      }
      if (e.ctrlKey && e.key === "y") {
        e.preventDefault();
        redo();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line
  }, [isOpen, historyIndex, history, showTextInput]);



  const toolButtons = [
    { id: TOOLS.PEN, icon: faPen, label: "قلم" },
    { id: TOOLS.BRUSH, icon: faPaintBrush, label: "فرشاة" },
    { id: TOOLS.HIGHLIGHTER, icon: faHighlighter, label: "ماركر" },
    { id: TOOLS.ERASER, icon: faEraser, label: "ممحاة" },
    { id: TOOLS.LINE, icon: faMinus, label: "خط" },
    { id: TOOLS.RECT, icon: faSquare, label: "مستطيل" },
    { id: TOOLS.CIRCLE, icon: faCircle, label: "دائرة" },
    { id: TOOLS.TEXT, icon: faFont, label: "نص" },
    { id: TOOLS.FILL, icon: faFill, label: "تعبئة" },
  ];

  const getCursorClass = () => {
    if (tool === TOOLS.ERASER) return "cursor-eraser";
    if (tool === TOOLS.FILL) return "cursor-fill";
    if (tool === TOOLS.TEXT) return "cursor-text";
    return "cursor-draw";
  };

  return (
    <div className="scratchpad-container" ref={containerRef}>
      {/* Scratchpad Panel */}
      <div
        className={`scratchpad-panel ${isOpen ? "open" : ""} ${isExpanded ? "expanded" : ""}`}
        ref={panelRef}
        role="dialog"
        aria-label="المسودة"
      >
        {/* Header */}
        <div className="sp-header">
          <div className="sp-header-left">
            <h3 className="sp-title">المسودة</h3>
          </div>
          <div className="sp-header-actions">
            <button className="sp-action-btn" onClick={undo} disabled={historyIndex <= 0} title="تراجع (Ctrl+Z)">
              <FontAwesomeIcon icon={faUndo} />
            </button>
            <button className="sp-action-btn" onClick={redo} disabled={historyIndex >= history.length - 1} title="إعادة (Ctrl+Y)">
              <FontAwesomeIcon icon={faRedo} />
            </button>
            {canDownload && (
              <button className="sp-action-btn" onClick={downloadCanvas} title="تحميل">
                <FontAwesomeIcon icon={faDownload} />
              </button>
            )}
            <button className="sp-action-btn" onClick={clearCanvas} title="مسح الكل">
              <FontAwesomeIcon icon={faTrash} />
            </button>
            <button className="sp-action-btn" onClick={() => setIsExpanded(!isExpanded)} title={isExpanded ? "تصغير" : "تكبير"}>
              <FontAwesomeIcon icon={isExpanded ? faCompress : faExpand} />
            </button>
            <button className="sp-action-btn sp-close-btn" onClick={() => handleSetIsOpen(false)} title="إغلاق">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="sp-toolbar">
          {/* Tools */}
          <div className="sp-tools-group">
            {toolButtons.map((t) => (
              <button
                key={t.id}
                className={`sp-tool-btn ${tool === t.id ? "active" : ""}`}
                onClick={() => setTool(t.id)}
                title={t.label}
              >
                <FontAwesomeIcon icon={t.icon} />
              </button>
            ))}
          </div>

          <div className="sp-toolbar-divider" />

          {/* Brush Size */}
          <div className="sp-size-group">
            <label className="sp-size-label">{brushSize}px</label>
            <input
              type="range"
              min="1"
              max="50"
              value={brushSize}
              onChange={(e) => setBrushSize(Number(e.target.value))}
              className="sp-size-slider"
            />
          </div>

          <div className="sp-toolbar-divider" />

          {/* Colors */}
          <div className="sp-colors-group">
            {COLORS.map((c) => (
              <button
                key={c}
                className={`sp-color-swatch ${color === c ? "active" : ""}`}
                style={{ backgroundColor: c }}
                onClick={() => setColor(c)}
                title={c}
              />
            ))}
            <div className="sp-custom-color-wrapper">
              <input
                type="color"
                value={customColor}
                onChange={(e) => {
                  setCustomColor(e.target.value);
                  setColor(e.target.value);
                }}
                className="sp-custom-color-input"
                title="لون مخصص"
              />
            </div>
          </div>
        </div>

        {/* Canvas Area */}
        <div className="sp-canvas-area" ref={canvasAreaRef}>
          <div className="sp-canvas-wrapper">
            <canvas
              ref={canvasRef}
              className={`sp-canvas ${getCursorClass()}`}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={() => {
                if (isDrawing) {
                  setIsDrawing(false);
                  const ctx = getCtx();
                  resetCtx(ctx);
                  saveToHistory();
                }
              }}
              onTouchStart={handleMouseDown}
              onTouchMove={handleMouseMove}
              onTouchEnd={handleMouseUp}
            />
            <canvas ref={overlayCanvasRef} className="sp-overlay-canvas" />

            {/* Text Input Overlay */}
            {showTextInput && textPos && (
              <div
                className="sp-text-input-overlay"
                style={{
                  left: `${(textPos.x / CANVAS_W) * 100}%`,
                  top: `${(textPos.y / CANVAS_H) * 100}%`,
                }}
              >
                <input
                  type="text"
                  className="sp-text-field"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleTextSubmit();
                    if (e.key === "Escape") setShowTextInput(false);
                  }}
                  autoFocus
                  placeholder="اكتب نصاً..."
                  style={{ color, fontSize: `${brushSize * 5 / (CANVAS_W / canvasRef.current?.getBoundingClientRect().width || 1)}px` }}
                  dir="rtl"
                />
              </div>
            )}
          </div>
        </div>

        {/* Status Bar */}
        <div className="sp-status-bar">
          <span className="sp-status-item">الأداة: {toolButtons.find(t => t.id === tool)?.label}</span>
          <span className="sp-status-item">الحجم: {brushSize}px</span>
          <span className="sp-status-item">
            اللون: <span className="sp-status-color" style={{ backgroundColor: color }} />
          </span>
        </div>
      </div>

      {/* FAB */}
      {!hideFab && (
        <button
          className={`scratchpad-fab ${isOpen ? "active" : ""}`}
          onClick={() => handleSetIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-label="تبديل المسودة"
        >
          <FontAwesomeIcon icon={isOpen ? faTimes : faPen} className="fab-icon" />
        </button>
      )}
    </div>
  );
};

export default Scratchpad;
